import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback, useEffect } from 'react';

import type { PaginationFilter } from '@/types/PaginationFilter';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getSimilarItemAlertsListItems,
    getSimilarItemAlertsLotIds,
    getSimilarItemAlertsTotalCount,
    isSimilarItemAlertsListEmpty,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { MySimilarItemAlertsFilter } from './components/MySimilarItemAlertsFilter';
import { MySimilarItemAlertsTable } from './components/MySimilarItemAlertsTable';

type Props = {
    filters: PaginationFilter;
};

/**
 * The For You "My Similar Item Alerts" tab. Lists the bidder's alerts from savedsearch-api,
 * hydrating each lotId into a card via item-summaries, and hands removal to the shared delete +
 * feedback modals.
 *
 * Search runs server-side: the keyword is passed through to `GET /saved-items` (SEAR-1781), so it
 * covers the whole collection rather than the loaded page, and the pager stays visible and pages
 * through the filtered set. `totalCount` is the filtered count; the onboarding empty state and the
 * placeholder read the unfiltered total instead, so neither is fooled by a no-match search.
 */
export const MySimilarItemAlerts = ({ filters }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();

    const alerts = useAppSelector(getSimilarItemAlertsListItems);
    const totalCount = useAppSelector(getSimilarItemAlertsTotalCount);
    const isEmpty = useAppSelector(isSimilarItemAlertsListEmpty);
    const lotIds = useAppSelector(getSimilarItemAlertsLotIds);

    // Hydrate the lot ids into the shared item-summary slice so each row can render its title and
    // thumbnail. A lot that can't be hydrated still shows via the row's stored lotTitle.
    useEffect(() => {
        if (lotIds.length) {
            dispatch(fetchItemSummariesIfNeeded({ identifier: 'SIMILAR-ITEM-ALERTS', itemIds: lotIds }));
        }
    }, [dispatch, lotIds]);

    const keyword = filters.keyword?.trim() ?? '';

    // The pager writes `page` straight to the URL (`shouldModifyQueryParams`) and ForYouPage
    // re-reads it from there, so there is no filter state to hold here. It requires an onChange
    // regardless — hence the deliberate no-op.
    const noopFilterChange = useCallback(() => {}, []);

    // Guarded on the keyword so a no-match search never falls back to the zero-alerts onboarding
    // copy — a bidder who has alerts but searched for something absent gets "nothing matched".
    if (isEmpty && !keyword) {
        return (
            <div className="mt-lg rounded-md bg-background-subdued p-lg text-center">
                <BodyPrimary className="block font-bold">
                    {formatMessage(['similarItemAlerts', 'emptyTitle'])}
                </BodyPrimary>
                <BodySecondary className="mt-2xs block">
                    {formatMessage(['similarItemAlerts', 'emptyBody'])}
                </BodySecondary>
            </div>
        );
    }

    const showPager = totalCount > Number(filters.pageSize);
    const table = <MySimilarItemAlertsTable alerts={alerts} />;

    return (
        <>
            <MySimilarItemAlertsFilter filters={filters} />
            <div className="mt-lg w-full">
                {keyword && !alerts.length ? (
                    <BodyPrimary className="my-lg block text-center">
                        {formatMessage(['similarItemAlerts', 'noSearchResults'], {
                            values: { keyword: filters.keyword },
                        })}
                    </BodyPrimary>
                ) : showPager ? (
                    <Pagination
                        onChange={noopFilterChange}
                        paginationFilter={{ ...filters, totalRecords: totalCount }}
                        shouldModifyQueryParams
                    >
                        {table}
                    </Pagination>
                ) : (
                    table
                )}
            </div>
        </>
    );
};
